import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Smartphone, X, Share, Plus, Check } from "lucide-react";
import { isIos, isStandalone } from "@/lib/pwa";

const C = { navy: "#121b28", sand: "#f4ebd7", terra: "#e37e5e" };
const BANNER_DISMISS_KEY = "et-install-dismissed";

/** Instalación PWA del itinerario: inyecta manifest dinámico + meta iOS, registra el SW
 *  (con auto-actualización), muestra un botón "Install" y un cartel inferior proactivo.
 *  Android/desktop = prompt nativo; iOS = instrucciones grandes y centradas. */
export default function PwaInstaller({ token, className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIos, setShowIos] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (!token) return undefined;

    // 1) Manifest dinámico (la app abrirá ESTE viaje al instalarse)
    const linkId = "et-pwa-manifest";
    let link = document.getElementById(linkId);
    if (!link) { link = document.createElement("link"); link.id = linkId; link.rel = "manifest"; document.head.appendChild(link); }
    link.href = `/api/public/trip/${token}/manifest.json`;

    // 2) Meta tags iOS / theme-color
    const created = [];
    const meta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const prev = el?.getAttribute("content");
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); created.push({ el, owned: true }); }
      else { created.push({ el, owned: false, prev }); }
      el.setAttribute("content", content);
    };
    meta("mobile-web-app-capable", "yes");
    meta("apple-mobile-web-app-capable", "yes");
    meta("apple-mobile-web-app-status-bar-style", "black-translucent");
    meta("apple-mobile-web-app-title", "Espíritu Travel");
    meta("theme-color", C.navy);

    const appleId = "et-apple-touch";
    let apple = document.getElementById(appleId);
    if (!apple) { apple = document.createElement("link"); apple.id = appleId; apple.rel = "apple-touch-icon"; apple.href = "/brand/et-apple-touch-180.png"; document.head.appendChild(apple); }

    // 3) Registrar el Service Worker + auto-actualización (que el cliente nunca quede en versión vieja)
    if ("serviceWorker" in navigator) {
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.register("/sw.js").catch(() => {});
      if (hadController) {
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (window.__etReloaded) return;
          window.__etReloaded = true;
          window.location.reload();
        });
      }
    }

    // 4) Eventos de instalación
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setShowBanner(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      document.getElementById(linkId)?.remove();
      created.forEach(({ el, owned, prev }) => { if (owned) el.remove(); else if (prev != null) el.setAttribute("content", prev); });
    };
  }, [token]);

  // Cartel proactivo: aparece a los pocos segundos si la app es instalable y no se descartó.
  useEffect(() => {
    if (installed) return undefined;
    if (!ios && !deferred) return undefined;
    let dismissed = false;
    try { dismissed = localStorage.getItem(BANNER_DISMISS_KEY) === "1"; } catch (_) {}
    if (dismissed) return undefined;
    const t = setTimeout(() => setShowBanner(true), 2500);
    return () => clearTimeout(t);
  }, [installed, ios, deferred]);

  const dismissBanner = () => {
    setShowBanner(false);
    try { localStorage.setItem(BANNER_DISMISS_KEY, "1"); } catch (_) {}
  };

  const triggerInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (_) {}
      setDeferred(null);
      setShowBanner(false);
    } else if (ios) {
      setShowIos(true);
      setShowBanner(false);
    }
  };

  if (installed) return null;
  const installable = !!deferred || ios;

  return (
    <>
      {installable && (
        <button onClick={triggerInstall} data-testid="pwa-install-btn"
          className={`flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm backdrop-blur transition-colors hover:bg-white/10 ${className}`}>
          <Smartphone className="h-4 w-4" /> <span className="hidden sm:inline">Install</span>
        </button>
      )}

      {/* Cartel inferior proactivo (portal a body para evitar el containing-block del header) */}
      {showBanner && installable && createPortal(
        <div className="fixed inset-x-0 bottom-0 z-[2147482000] flex justify-center p-3 print:hidden" data-testid="pwa-install-banner">
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl p-4 shadow-2xl" style={{ background: C.navy, color: C.sand }}>
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl" style={{ background: C.terra }}>
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-bold leading-tight" style={{ fontFamily: "'Kanit', sans-serif" }}>Add this trip to your phone</p>
              <p className="text-sm leading-snug opacity-80">Open it like an app — one tap.</p>
            </div>
            <button onClick={triggerInstall} data-testid="pwa-install-banner-cta"
              className="flex-none rounded-full px-4 py-2.5 text-sm font-bold text-white" style={{ background: C.terra }}>
              {ios ? "Show me" : "Install"}
            </button>
            <button onClick={dismissBanner} data-testid="pwa-install-banner-dismiss"
              className="flex-none rounded-full p-1.5 opacity-60 hover:opacity-100" aria-label="Dismiss">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Instrucciones iOS: centrado, grande y simple */}
      {showIos && createPortal(
        <div
          className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          data-testid="pwa-ios-modal"
          onClick={() => setShowIos(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 pb-8 text-left shadow-2xl sm:p-8"
            style={{ color: C.navy }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-2xl font-extrabold leading-tight" style={{ fontFamily: "'Kanit', sans-serif" }}>
                  Install this trip
                </h3>
                <p className="mt-1 text-base text-stone-500">3 easy steps</p>
              </div>
              <button
                onClick={() => setShowIos(false)}
                data-testid="pwa-ios-close"
                className="-mr-1 -mt-1 rounded-full p-2 text-stone-400 hover:bg-stone-100"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <ol className="space-y-5">
              <li className="flex items-center gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: C.terra }}>1</span>
                <span className="flex items-center gap-2 text-lg leading-snug">
                  Tap <Share className="h-6 w-6 flex-none" style={{ color: C.terra }} /> <strong>Share</strong>
                </span>
              </li>
              <li className="flex items-center gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: C.terra }}>2</span>
                <span className="flex items-center gap-2 text-lg leading-snug">
                  Tap <Plus className="h-6 w-6 flex-none" style={{ color: C.terra }} /> <strong>Add to Home Screen</strong>
                </span>
              </li>
              <li className="flex items-center gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: C.terra }}>3</span>
                <span className="flex items-center gap-2 text-lg leading-snug">
                  Tap <strong>Add</strong> <Check className="h-6 w-6 flex-none" style={{ color: C.terra }} /> Done!
                </span>
              </li>
            </ol>

            <button
              onClick={() => setShowIos(false)}
              data-testid="pwa-ios-got-it"
              className="mt-7 w-full rounded-full py-4 text-lg font-bold text-white transition-opacity hover:opacity-90"
              style={{ background: C.navy }}
            >
              Got it
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
